import React, { useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const InvoiceSettingsTab = () => {
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    defaultTerms: `Payment is due within 30 days from the invoice date.
Late payments are subject to a 1.5% monthly finance charge.
Please include invoice number with your payment.`,
    defaultNotes: 'Thank you for your business!',
    defaultFooter: `All services are guaranteed for 90 days.
Please contact us immediately if you have any questions about this invoice.`,
    paymentTerms: [
      { id: 1, name: 'Net 30', days: 30 },
      { id: 2, name: 'Net 15', days: 15 },
      { id: 3, name: 'Due on Receipt', days: 0 }
    ],
    taxRates: [
      { id: 1, name: 'Standard Rate', rate: 8.25 },
      { id: 2, name: 'Reduced Rate', rate: 4.5 },
      { id: 3, name: 'Zero Rate', rate: 0 }
    ]
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('companies')
        .update({
          metadata: {
            invoice_settings: settings
          }
        })
        .eq('id', user.id);

      if (error) throw error;

      // Show success message
      alert('Invoice settings saved successfully!');
    } catch (err) {
      console.error('Error saving invoice settings:', err);
      alert('Failed to save invoice settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addPaymentTerm = () => {
    const newId = Math.max(0, ...settings.paymentTerms.map(t => t.id)) + 1;
    setSettings({
      ...settings,
      paymentTerms: [
        ...settings.paymentTerms,
        { id: newId, name: '', days: 0 }
      ]
    });
  };

  const addTaxRate = () => {
    const newId = Math.max(0, ...settings.taxRates.map(t => t.id)) + 1;
    setSettings({
      ...settings,
      taxRates: [
        ...settings.taxRates,
        { id: newId, name: '', rate: 0 }
      ]
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-1">Invoice Settings</h2>
        <p className="text-gray-600">Configure default settings for your invoices</p>
      </div>

      {/* Default Terms & Conditions */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Default Terms & Conditions</h3>
        <textarea
          value={settings.defaultTerms}
          onChange={(e) => setSettings({ ...settings, defaultTerms: e.target.value })}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter your default terms and conditions..."
        />
      </div>

      {/* Default Notes */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Default Notes</h3>
        <textarea
          value={settings.defaultNotes}
          onChange={(e) => setSettings({ ...settings, defaultNotes: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter your default invoice notes..."
        />
      </div>

      {/* Default Footer */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Default Footer</h3>
        <textarea
          value={settings.defaultFooter}
          onChange={(e) => setSettings({ ...settings, defaultFooter: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter your default invoice footer..."
        />
      </div>

      {/* Payment Terms */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-900">Payment Terms</h3>
          <button
            onClick={addPaymentTerm}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Term
          </button>
        </div>
        <div className="space-y-3">
          {settings.paymentTerms.map((term, index) => (
            <div key={term.id} className="flex items-center space-x-4">
              <input
                type="text"
                value={term.name}
                onChange={(e) => {
                  const newTerms = [...settings.paymentTerms];
                  newTerms[index].name = e.target.value;
                  setSettings({ ...settings, paymentTerms: newTerms });
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Term name (e.g., Net 30)"
              />
              <input
                type="number"
                value={term.days}
                onChange={(e) => {
                  const newTerms = [...settings.paymentTerms];
                  newTerms[index].days = parseInt(e.target.value);
                  setSettings({ ...settings, paymentTerms: newTerms });
                }}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Days"
              />
              <button
                onClick={() => {
                  setSettings({
                    ...settings,
                    paymentTerms: settings.paymentTerms.filter(t => t.id !== term.id)
                  });
                }}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tax Rates */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-900">Tax Rates</h3>
          <button
            onClick={addTaxRate}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Tax Rate
          </button>
        </div>
        <div className="space-y-3">
          {settings.taxRates.map((tax, index) => (
            <div key={tax.id} className="flex items-center space-x-4">
              <input
                type="text"
                value={tax.name}
                onChange={(e) => {
                  const newRates = [...settings.taxRates];
                  newRates[index].name = e.target.value;
                  setSettings({ ...settings, taxRates: newRates });
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Tax rate name"
              />
              <div className="relative w-32">
                <input
                  type="number"
                  value={tax.rate}
                  onChange={(e) => {
                    const newRates = [...settings.taxRates];
                    newRates[index].rate = parseFloat(e.target.value);
                    setSettings({ ...settings, taxRates: newRates });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Rate"
                  step="0.01"
                  min="0"
                  max="100"
                />
                <span className="absolute right-3 top-2 text-gray-500">%</span>
              </div>
              <button
                onClick={() => {
                  setSettings({
                    ...settings,
                    taxRates: settings.taxRates.filter(t => t.id !== tax.id)
                  });
                }}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default InvoiceSettingsTab;